import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  BookOpen,
  Wallet,
  MessageSquare,
  BookMarked,
  LogOut,
  ClipboardList,
} from 'lucide-react';
import { BRAND_NAME, logoImage } from '../../config/brand';
import useScrollToTop from '../../hooks/useScrollToTop';

const menuItems = [
  {
    path: '/student-portal',
    label: 'كورساتي وحصصي',
    shortLabel: 'كورساتي وحصصي',
    icon: BookMarked,
    end: true,
    activeClass: 'bg-green-700 text-white shadow-md shadow-green-900/25 ring-2 ring-green-800/30',
    idleClass: 'bg-green-50 text-green-800 border border-green-200 hover:bg-green-100',
  },
  {
    path: '/student-portal/browse',
    label: 'استكشف',
    shortLabel: 'استكشف',
    icon: BookOpen,
    activeClass: 'bg-teal-700 text-white shadow-md shadow-teal-900/25 ring-2 ring-teal-800/30',
    idleClass: 'bg-teal-50 text-teal-800 border border-teal-200 hover:bg-teal-100',
  },
  {
    path: '/student-portal/wallet',
    label: 'محفظتي',
    shortLabel: 'محفظتي',
    icon: Wallet,
    activeClass: 'bg-gold-500 text-green-950 shadow-md shadow-gold-500/30 ring-2 ring-gold-400/40',
    idleClass: 'bg-gold-50 text-gold-800 border border-gold-200 hover:bg-gold-100',
  },
  {
    path: '/student-portal/forum',
    label: 'المنتدى',
    shortLabel: 'المنتدى',
    icon: MessageSquare,
    activeClass: 'bg-emerald-700 text-white shadow-md shadow-emerald-900/25 ring-2 ring-emerald-800/30',
    idleClass: 'bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100',
  },
  {
    path: '/student-portal/my-grades',
    label: 'درجاتي',
    shortLabel: 'درجاتي',
    icon: ClipboardList,
    activeClass: 'bg-amber-600 text-white shadow-md shadow-amber-700/25 ring-2 ring-amber-500/40',
    idleClass: 'bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100',
  },
];

const StudentPortalLayout: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  useScrollToTop();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f4f6f4] font-cairo" dir="rtl">
      <header className="sticky top-0 z-50 border-b border-green-100/80 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-2 px-3 sm:h-16 sm:px-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-sm sm:h-11 sm:w-11">
            <img
              src={logoImage}
              alt={BRAND_NAME}
              className="h-full w-full object-contain"
            />
          </div>

          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {currentUser?.name && (
              <span className="hidden max-w-[10rem] truncate text-sm font-semibold text-green-900 sm:inline md:max-w-xs">
                {currentUser.name}
              </span>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex min-h-10 items-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 sm:min-h-11 sm:px-3 sm:text-sm"
              aria-label="تسجيل الخروج"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="hidden min-[400px]:inline">خروج</span>
            </button>
          </div>
        </div>

        <nav
          className="border-t border-green-50 bg-gradient-to-b from-white to-green-50/40"
          aria-label="قائمة بوابة الطالب"
        >
          <div className="mx-auto max-w-6xl px-3 py-2.5 sm:px-6 sm:py-3">
            <div className="-mx-1 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:justify-start sm:overflow-visible sm:pb-0">
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-all sm:gap-2 sm:px-3.5 sm:text-sm ${
                      isActive ? item.activeClass : item.idleClass
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0 sm:h-[1.125rem] sm:w-[1.125rem]" />
                  <span className="whitespace-nowrap sm:hidden">{item.shortLabel}</span>
                  <span className="hidden whitespace-nowrap sm:inline">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </nav>
      </header>

      <main className="mx-auto min-h-[calc(100vh-8rem)] max-w-6xl overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default StudentPortalLayout;
